import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { theme } from '../styles/theme.js';

/**
 * App-first layout for App photons.
 *
 * The app UI fills the viewport. A labeled divider hints that methods
 * are available below. Fullscreen button lives in the context bar (external).
 *
 * Popout mode makes the app viewport fullscreen — no second renderer needed.
 * This avoids Safari blank iframe issues caused by duplicate iframes in shadow DOM.
 */
@customElement('app-layout')
export class AppLayout extends LitElement {
  static styles = [
    theme,
    css`
      :host {
        display: block;
      }

      :host([hide-below]) .scroll-divider,
      :host([hide-below]) .below-fold {
        display: none;
      }

      /* App tab mode: fill the available flex height instead of using min-height */
      :host([hide-below]) {
        flex: 1;
        min-height: 0;
        height: 100%;
      }

      :host([hide-below]) .app-viewport {
        min-height: unset;
        height: 100%;
        display: flex;
        flex-direction: column;
        /* Remove rounded corners and clipping — we fill the full viewport */
        border-radius: 0;
        overflow: visible;
      }

      :host([hide-below]) .app-content {
        overflow: hidden;
        flex: 1;
        min-height: 0;
      }

      .app-viewport {
        min-height: calc(100vh - 140px);
        border-radius: var(--radius-md);
        overflow: hidden;
      }

      /* Popout mode: app-viewport goes fullscreen */
      :host([popped-out]) .app-viewport {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        min-height: unset;
        border-radius: 0;
        z-index: 9999;
        background: var(--bg-app, #0a0a12);
        display: flex;
        flex-direction: column;
      }

      .popout-header {
        display: none;
        align-items: center;
        padding: var(--space-sm) var(--space-md);
        border-bottom: 1px solid var(--border-glass);
        background: var(--bg-glass);
        flex-shrink: 0;
      }

      :host([popped-out]) .popout-header {
        display: flex;
      }

      .popout-header .app-name {
        font-weight: 600;
        font-size: var(--text-md);
        color: var(--t-primary);
        flex: 1;
        min-width: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .popout-close {
        background: var(--bg-glass);
        border: 1px solid var(--border-glass);
        color: var(--t-muted);
        padding: 4px 12px;
        border-radius: var(--radius-sm);
        cursor: pointer;
        font-size: var(--text-md);
        transition: all 0.2s ease;
      }

      .popout-close:hover {
        color: var(--t-primary);
        background: var(--bg-glass-strong);
        border-color: var(--accent-primary);
      }

      .app-content {
        flex: 1;
        overflow: auto;
      }

      :host([popped-out]) .scroll-divider,
      :host([popped-out]) .below-fold {
        display: none;
      }

      .scroll-divider {
        position: relative;
        text-align: center;
        margin: var(--space-lg) 0;
      }

      .scroll-divider::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        height: 1px;
        background: var(--border-glass);
      }

      .scroll-divider span {
        position: relative;
        display: inline-block;
        padding: 0 var(--space-md);
        background: var(--bg-app, #0a0a12);
        color: var(--t-muted);
        font-size: var(--text-sm);
        opacity: 0.6;
      }

      .below-fold {
        padding-top: var(--space-lg);
      }
    `,
  ];

  @property({ type: String })
  photonName = '';

  @property({ type: String })
  photonIcon = '';

  @property({ type: Boolean, reflect: true, attribute: 'hide-below' })
  hideBelow = false;

  @state()
  private _poppedOut = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this._handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._handleKeydown);
  }

  private _handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this._poppedOut) {
      this._poppedOut = false;
      this.removeAttribute('popped-out');
    }
  };

  render() {
    return html`
      <div class="app-viewport">
        <div class="popout-header">
          <span class="app-name"
            >${this.photonIcon ? html`${this.photonIcon} ` : ''}${this.photonName}</span
          >
          <button class="popout-close" @click=${() => this.togglePopout()}>
            ✕ Close <kbd>Esc</kbd>
          </button>
        </div>
        <div class="app-content">
          <slot name="app"></slot>
        </div>
      </div>

      <div class="scroll-divider">
        <span>&darr; scroll down for methods, prompts &amp; resources</span>
      </div>

      <div class="below-fold">
        <slot name="below-fold"></slot>
      </div>
    `;
  }

  togglePopout() {
    this._poppedOut = !this._poppedOut;
    if (this._poppedOut) {
      this.setAttribute('popped-out', '');
    } else {
      this.removeAttribute('popped-out');
    }
  }
}
